import { SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { SessionsDataTable } from "@/components/sessions/sessions.data-table";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sessionsApi } from "@/services/sessions/sessions.api";
import { $fetch } from "@/lib/fetch";
import { type SessionFormDto } from "slde-api/dto-schemas";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { type SessionEntity } from "slde-api/db-schemas";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { SessionForm } from "@/components/sessions/session.form";
import { useResources } from "@/providers/resources.provider";

export default function SessionsPage() {
  const queryClient = useQueryClient();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [sessionToUpdate, setSessionToUpdate] = useState<SessionEntity | null>(
    null,
  );

  const {
    sessions,
    createSession,
    deleteSession,
    isCreatingSession,
    isDeletingSession,
  } = useResources();

  const { mutate: updateSession, isPending: isUpdatingSession } = useMutation({
    mutationFn: async ({ id, ...form }: SessionEntity) =>
      await $fetch(sessionsApi.session.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano sesje");
      setSessionToUpdate(null);
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji sesji: ${e.message}`);
      }
      console.error(e);
    },
  });

  return (
    <div>
      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-base font-medium">Zarządzanie sesjami</h1>
          <div className="ml-auto flex items-center gap-2">
            <Button onClick={() => setCreateDialogOpen(true)}>
              Dodaj sesję
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
        <SessionsDataTable
          data={sessions}
          actions={{
            onUpdateClick: (session) => setSessionToUpdate(session),
            onDeleteClick: (id) => deleteSession({ id }),
          }}
        />
      </div>

      <Dialog
        open={createDialogOpen || !!sessionToUpdate}
        onOpenChange={(open) => {
          setCreateDialogOpen(open);
          setSessionToUpdate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {!!sessionToUpdate ? "Edytuj sesję" : "Dodaj sesję"}
            </DialogTitle>
          </DialogHeader>

          <SessionForm
            onSubmit={(form) =>
              !!sessionToUpdate
                ? updateSession({ id: sessionToUpdate.id, ...form })
                : createSession(form)
            }
            defaultValues={sessionToUpdate}
            disabled={
              isCreatingSession || isUpdatingSession || isDeletingSession
            }
            loading={
              isCreatingSession || isUpdatingSession || isDeletingSession
            }
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
